// Deterministic id construction.
//
// Every string in this file becomes a DynamoDB primary key. Changing a format
// silently orphans existing rows (a reservation that no longer matches lets a
// payment post twice), so each helper is pinned by an exact-string test.

import type { PmsEntity, PmsMode, PmsOp, PmsProvider } from './types';

/**
 * Idempotency key for a job. The date component keeps same-day retries on one
 * row while allowing a genuinely new operation tomorrow.
 *
 * NOT for payments — see paymentJobId.
 */
export function jobId(op: PmsOp, binsID: string, dateISO: string): string {
  return `${op}:${binsID}:${dateISO.slice(0, 10)}`;
}

/**
 * Idempotency key for a payment. Deliberately has NO date component: one
 * LedgerEntry is one payment forever. With a date, a retry the next day would
 * create a second job for the same ledger row, which is the double-post this
 * design exists to prevent.
 */
export function paymentJobId(ledgerEntryID: string): string {
  return `pay:${ledgerEntryID}`;
}

/** Id of the row mapping one of our ids to a vendor id. */
export function externalRefId(provider: PmsProvider, entity: PmsEntity, binsID: string): string {
  return `${provider}#${entity}#${binsID}`;
}

/**
 * Id of the row we write BEFORE posting a payment, so a concurrent or repeated
 * attempt finds it and stops (Amplify's create resolver uses
 * attribute_not_exists(id), so the second write simply fails).
 *
 * Shadow-mode reservations are prefixed so they cannot block the real payment
 * later: without this, every ledger entry touched during the shadow period
 * would look "already posted" once the integration goes live.
 */
export function paymentReservationId(
  provider: PmsProvider,
  ledgerEntryID: string,
  mode: PmsMode,
): string {
  const base = externalRefId(provider, 'payment', ledgerEntryID);
  return mode === 'shadow' ? `shadow#${base}` : base;
}

/** A move-in is identified by the pair, since one tenant can occupy several units. */
export function moveInBinsID(binsTenantID: string, binsUnitID: string): string {
  return `${binsTenantID}:${binsUnitID}`;
}

/** Queue partition for the drainer's keyed query: one query returns due work. */
export function queueKey(provider: PmsProvider, status: string): string {
  return `${provider}#${status}`;
}
